import React from 'react';
import { LandingHeader } from '../../../shared/components/layout/LandingHeader';
import { PageHeader } from '../../../shared/components/layout/pageHeader';
import { Footer } from '../../../shared/components/layout/Footer';

export default function Terms() {
  return (
    <div className="min-h-screen flex flex-col">
      <LandingHeader color="#361E06" />
      
      <div className="flex-grow max-w-3xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
        <PageHeader
          headline="Terms of Service"
          subheadline="Last updated: March 2024"
        />

        <div className="prose prose-lg mt-6 text-gray-600 max-w-none">
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-sm p-6 space-y-4">
              <h2 className="text-xl font-semibold text-[#361E06] border-b border-gray-200 pb-3">1. Agreement to Terms</h2>
              <p className="text-base leading-relaxed">
                By accessing or using Theos ("the Service"), you agree to be bound by these Terms of Service ("Terms"). If you disagree with any part of the Terms, you do not have permission to access or use the Service. These Terms constitute a legally binding agreement between you and Theos ("we," "us," or "our").
              </p>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-6 space-y-4">
              <h2 className="text-xl font-semibold text-[#361E06] border-b border-gray-200 pb-3">2. Description of Service</h2>
              <p className="text-base leading-relaxed">
                Theos is an AI-powered Bible study companion that helps users explore and understand scripture through interactive conversations and personalized insights. The Service includes:
              </p>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li>Web and mobile applications for iOS and Android platforms</li>
                <li>AI-powered conversation features for Bible study</li>
                <li>Bible reading and verse exploration tools</li>
                <li>Bookmarking and note-taking capabilities</li>
                <li>Public chat sharing functionality</li>
                <li>Exploration and discovery features</li>
                <li>Profile customization options</li>
              </ul>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-6 space-y-4">
              <h2 className="text-xl font-semibold text-[#361E06] border-b border-gray-200 pb-3">3. User Accounts</h2>
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-medium text-[#361E06] mb-2">3.1 Account Creation</h3>
                  <p className="text-base leading-relaxed">
                    When you create an account, you must provide accurate and complete information. You may create an account directly or using Google Sign-in. You are responsible for maintaining the security of your account credentials and for all activities that occur under your account.
                  </p>
                </div>
                <div>
                  <h3 className="text-lg font-medium text-[#361E06] mb-2">3.2 Account Security</h3>
                  <p className="text-base leading-relaxed">
                    You agree to:
                  </p>
                  <ul className="list-disc pl-6 space-y-1 mt-2">
                    <li>Maintain the confidentiality of your password</li>
                    <li>Notify us immediately of any unauthorized access</li>
                    <li>Not share your account credentials with third parties</li>
                    <li>Update your information to ensure it remains accurate and current</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-6 space-y-4">
              <h2 className="text-xl font-semibold text-[#361E06] border-b border-gray-200 pb-3">4. Subscription Terms</h2>
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-medium text-[#361E06] mb-2">4.1 Service Tiers</h3>
                  <p className="text-base leading-relaxed">
                    Theos offers both free and premium features. Premium features require a paid subscription and include:
                  </p>
                  <ul className="list-disc pl-6 space-y-1 mt-2">
                    <li>Unlimited AI conversations</li>
                    <li>Advanced Bible study tools</li>
                    <li>Enhanced exploration features</li>
                    <li>Additional customization options</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-lg font-medium text-[#361E06] mb-2">4.2 Billing and Cancellation</h3>
                  <p className="text-base leading-relaxed">
                    Subscription terms:
                  </p>
                  <ul className="list-disc pl-6 space-y-1 mt-2">
                    <li>Subscription fees are billed in advance on a recurring basis</li>
                    <li>Payment will be charged to your chosen payment method</li>
                    <li>Subscriptions automatically renew unless cancelled</li>
                    <li>You can cancel your subscription at any time through your account settings or platform-specific subscription management</li>
                    <li>Cancellation will take effect at the end of the current billing period</li>
                    <li>We do not provide refunds for partial subscription periods</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-lg font-medium text-[#361E06] mb-2">4.3 Platform-Specific Terms</h3>
                  <p className="text-base leading-relaxed">
                    For mobile subscriptions:
                  </p>
                  <ul className="list-disc pl-6 space-y-1 mt-2">
                    <li>iOS subscriptions are managed through Apple's App Store</li>
                    <li>Android subscriptions are managed through Google Play</li>
                    <li>Platform-specific terms and conditions apply to these purchases</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-6 space-y-4">
              <h2 className="text-xl font-semibold text-[#361E06] border-b border-gray-200 pb-3">5. Intellectual Property Rights</h2>
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-medium text-[#361E06] mb-2">5.1 Our Intellectual Property</h3>
                  <p className="text-base leading-relaxed">
                    The Service and its original content (excluding User Content), features, and functionality are owned by Theos and are protected by:
                  </p>
                  <ul className="list-disc pl-6 space-y-1 mt-2">
                    <li>International copyright laws</li>
                    <li>Trademark laws</li>
                    <li>Patent laws</li>
                    <li>Trade secret laws</li>
                    <li>Other intellectual property rights</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-lg font-medium text-[#361E06] mb-2">5.2 Bible Text and Resources</h3>
                  <p className="text-base leading-relaxed">
                    Bible texts and related resources may be subject to third-party copyright and usage restrictions. You agree to respect all applicable copyright notices and restrictions when using these materials.
                  </p>
                </div>
                <div>
                  <h3 className="text-lg font-medium text-[#361E06] mb-2">5.3 AI-Generated Content</h3>
                  <p className="text-base leading-relaxed">
                    All responses and content generated by our AI system are owned by Theos. While you may use this content for personal study, you may not:
                  </p>
                  <ul className="list-disc pl-6 space-y-1 mt-2">
                    <li>Redistribute AI-generated content commercially</li>
                    <li>Claim ownership of AI-generated responses</li>
                    <li>Modify or create derivative works without permission</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-6 space-y-4">
              <h2 className="text-xl font-semibold text-[#361E06] border-b border-gray-200 pb-3">6. User Content</h2>
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-medium text-[#361E06] mb-2">6.1 Content Ownership</h3>
                  <p className="text-base leading-relaxed">
                    You retain ownership of any content you submit through the Service, including:
                  </p>
                  <ul className="list-disc pl-6 space-y-1 mt-2">
                    <li>Chat messages and questions</li>
                    <li>Notes and annotations</li>
                    <li>Bookmarks and collections</li>
                    <li>Profile information</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-lg font-medium text-[#361E06] mb-2">6.2 Content License</h3>
                  <p className="text-base leading-relaxed">
                    By submitting content, you grant us a worldwide, non-exclusive, royalty-free license to:
                  </p>
                  <ul className="list-disc pl-6 space-y-1 mt-2">
                    <li>Use, reproduce, and store your content</li>
                    <li>Modify and adapt content for technical purposes</li>
                    <li>Display content within the Service</li>
                    <li>Analyze content to improve the Service</li>
                    <li>Share content according to your privacy settings</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-lg font-medium text-[#361E06] mb-2">6.3 Content Restrictions</h3>
                  <p className="text-base leading-relaxed">
                    You agree not to submit content that:
                  </p>
                  <ul className="list-disc pl-6 space-y-1 mt-2">
                    <li>Violates any laws or regulations</li>
                    <li>Infringes on third-party rights</li>
                    <li>Is harmful, abusive, or inappropriate</li>
                    <li>Contains malware or harmful code</li>
                    <li>Interferes with the Service's operation</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-6 space-y-4">
              <h2 className="text-xl font-semibold text-[#361E06] border-b border-gray-200 pb-3">7. Acceptable Use</h2>
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-medium text-[#361E06] mb-2">7.1 Prohibited Activities</h3>
                  <p className="text-base leading-relaxed">
                    You agree not to:
                  </p>
                  <ul className="list-disc pl-6 space-y-1 mt-2">
                    <li>Use the Service for any unlawful purpose</li>
                    <li>Attempt to gain unauthorized access</li>
                    <li>Interfere with or disrupt the Service</li>
                    <li>Circumvent any limitations or security measures</li>
                    <li>Scrape or collect data without permission</li>
                    <li>Impersonate others or provide false information</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-lg font-medium text-[#361E06] mb-2">7.2 Technical Restrictions</h3>
                  <p className="text-base leading-relaxed">
                    You agree not to:
                  </p>
                  <ul className="list-disc pl-6 space-y-1 mt-2">
                    <li>Exceed API rate limits</li>
                    <li>Modify or reverse engineer the Service</li>
                    <li>Create unauthorized derivative works</li>
                    <li>Bypass or modify client applications</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-6 space-y-4">
              <h2 className="text-xl font-semibold text-[#361E06] border-b border-gray-200 pb-3">8. Data Protection and Privacy</h2>
              <p className="text-base leading-relaxed">
                Your use of the Service is also governed by our Privacy Policy. By using the Service, you agree to:
              </p>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li>Our data collection and processing practices</li>
                <li>The use of cookies and tracking technologies</li>
                <li>Cross-border data transfers as necessary</li>
                <li>Marketing communications (subject to your preferences)</li>
              </ul>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-6 space-y-4">
              <h2 className="text-xl font-semibold text-[#361E06] border-b border-gray-200 pb-3">9. Disclaimer of Warranties</h2>
              <p className="text-base leading-relaxed">
                The Service is provided "as is" and "as available" without warranties of any kind, either express or implied, including but not limited to:
              </p>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li>Merchantability</li>
                <li>Fitness for a particular purpose</li>
                <li>Non-infringement</li>
                <li>Error-free or uninterrupted operation</li>
              </ul>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-6 space-y-4">
              <h2 className="text-xl font-semibold text-[#361E06] border-b border-gray-200 pb-3">10. Limitation of Liability</h2>
              <p className="text-base leading-relaxed">
                To the maximum extent permitted by law:
              </p>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li>We shall not be liable for any indirect, incidental, special, consequential, or punitive damages</li>
                <li>Our total liability shall not exceed the amount paid by you for the Service in the past 12 months</li>
                <li>We are not liable for any loss of data, profits, or business opportunities</li>
              </ul>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-6 space-y-4">
              <h2 className="text-xl font-semibold text-[#361E06] border-b border-gray-200 pb-3">11. Indemnification</h2>
              <p className="text-base leading-relaxed">
                You agree to indemnify and hold harmless Theos and its officers, directors, employees, and agents from any claims, damages, losses, liabilities, costs, and expenses (including reasonable attorneys' fees) arising from:
              </p>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li>Your use of the Service</li>
                <li>Your violation of these Terms</li>
                <li>Your User Content</li>
                <li>Your interaction with other users</li>
              </ul>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-6 space-y-4">
              <h2 className="text-xl font-semibold text-[#361E06] border-b border-gray-200 pb-3">12. Changes to Terms</h2>
              <p className="text-base leading-relaxed">
                We reserve the right to modify these Terms at any time. We will:
              </p>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li>Notify users of material changes via email or in-app notification</li>
                <li>Update the "last updated" date at the top of these Terms</li>
                <li>Provide a reasonable time period before changes take effect</li>
                <li>Obtain consent where required by law</li>
              </ul>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-6 space-y-4">
              <h2 className="text-xl font-semibold text-[#361E06] border-b border-gray-200 pb-3">13. Governing Law</h2>
              <p className="text-base leading-relaxed">
                These Terms shall be governed by and construed in accordance with the laws of the United States, without regard to its conflict of law provisions. Any disputes shall be resolved in the courts of competent jurisdiction.
              </p>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-6 space-y-4">
              <h2 className="text-xl font-semibold text-[#361E06] border-b border-gray-200 pb-3">14. Severability</h2>
              <p className="text-base leading-relaxed">
                If any provision of these Terms is found to be unenforceable or invalid, that provision shall be limited or eliminated to the minimum extent necessary so that these Terms shall otherwise remain in full force and effect and enforceable.
              </p>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-6 space-y-4">
              <h2 className="text-xl font-semibold text-[#361E06] border-b border-gray-200 pb-3">15. Contact Information</h2>
              <p className="text-base leading-relaxed">
                If you have any questions about these Terms, please contact us at support@theos.chat.
              </p>
            </div>
          </div>

          <div className="mt-6 text-sm text-gray-500 text-center">
            <p>For any questions about these Terms, please contact us at support@theos.chat</p>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
} 